import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, SpecraActionList, SpecraBadge, SpecraButton, SpecraDataTable, SpecraInlineBanner, SpecraOverlayDrawer, SpecraSectionHeader, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { contractsClient } from '../../Delivery/Registries/Contracts/api';
import { useSetLandscapeSeat, useSuggestSeatRounds, useUpsertSeatRound } from '../hooks/useLandscapeSeats';
import { MergeSeatDialog, useUndoSeatMerge } from './MergeSeatDialog';
import { SeatDialog } from './SeatDialog';
import { SeatRoundForm } from './SeatRoundForm';
import { SeatRoundsDialog } from './SeatRoundsDialog';
import { SplitSeatDialog } from './SplitSeatDialog';
import { monthYear, toYear } from './seatTime';
const T = Testids.seatAdmin;
export function ManageSeatsDrawer({ landscapeId, landscapeName, seats, canEdit, onClose }) {
    const [action, setAction] = useState();
    const [adding, setAdding] = useState(false);
    const [seatId, setSeatId] = useState(seats[0]?.id ?? '');
    const selected = seats.find((s)=>s.id === seatId) ?? seats[0];
    const shown = seats.find((s)=>s.id === action?.id);
    const suggestions = useSuggestSeatRounds({
        landscapeId,
        seatId: selected?.id
    });
    const membership = useSetLandscapeSeat();
    const upsert = useUpsertSeatRound();
    const [error, setError] = useState('');
    const undo = useUndoSeatMerge(setError);
    const [accepting, setAccepting] = useState(false);
    const [unmatched, setUnmatched] = useState();
    const accept = async (round, reason)=>{
        if (!selected || accepting) return;
        setAccepting(true);
        setError('');
        try {
            const candidate = {
                ...round,
                roundIndex: Math.max(0, ...selected.rounds.map((r)=>r.roundIndex)) + 1,
                vehicleId: round.vehicleId || selected.vehicleId
            };
            if (round.registryContractId && !round.holderCompanyId && round.holderKind === 'unknown') {
                const evidence = await contractsClient.listRegistryContractEvidence({
                    contractId: round.registryContractId,
                    limit: 100
                });
                let name = round.holderLabel || '';
                let uei = /uei:([A-Za-z0-9]+)/i.exec(reason)?.[1] ?? '';
                for (const e of evidence.evidence.filter((e)=>!e.proposal)){
                    const result = await contractsClient.getRegistryContractObservation({
                        id: e.observationId
                    });
                    const raw = JSON.parse(result.observation?.originalJson || '{}');
                    name || (name = raw.recipient?.recipient_name || raw.recipient_name || raw.awardee_name || '');
                    uei || (uei = raw.recipient?.recipient_uei || result.observation?.identity?.partyKey?.replace(/^uei:/i, '') || '');
                    if (name && uei) break;
                }
                setUnmatched({
                    seat: selected,
                    round: candidate,
                    awardee: {
                        kind: 'COMPANY',
                        name,
                        uei
                    }
                });
            } else await upsert.mutateAsync({
                seatId: selected.id,
                round: candidate
            });
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally{
            setAccepting(false);
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
                open: true,
                modal: false,
                title: `Seats in ${landscapeName}`,
                testId: T.drawer,
                onOpenChange: (v)=>{
                    if (!v) onClose();
                },
                contentProps: {
                    onInteractOutside: (e)=>e.preventDefault()
                },
                footerProps: {
                    cancelLabel: 'Close',
                    onCancel: onClose
                },
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                            tone: "danger",
                            "data-testid": T.error,
                            children: error
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": T.addSeat,
                            onClick: ()=>setAdding(true),
                            children: "Add seat"
                        }),
                        /*#__PURE__*/ _jsx(SpecraDataTable, {
                            rows: seats,
                            getRowId: (s)=>s.id,
                            getRowProps: ()=>({
                                    className: '',
                                    'data-testid': T.seatRow
                                }),
                            columns: [
                                {
                                    header: 'Name',
                                    cell: (s)=>{
                                        const mergedAt = toYear(s.mergedOn);
                                        return /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    children: s.name
                                                }),
                                                s.mergedIntoSeatId && /*#__PURE__*/ _jsxs(SpecraBadge, {
                                                    variant: "neutral",
                                                    children: [
                                                        "Merged into",
                                                        ' ',
                                                        seats.find((other)=>other.id === s.mergedIntoSeatId)?.name ?? s.mergedIntoSeatId,
                                                        ' ',
                                                        "· ",
                                                        mergedAt !== null ? monthYear(mergedAt) : '—'
                                                    ]
                                                })
                                            ]
                                        });
                                    }
                                },
                                {
                                    header: 'Customer',
                                    cell: (s)=>s.customerLabel || '—'
                                },
                                {
                                    header: 'Vehicle',
                                    cell: (s)=>s.vehicleLabel || '—'
                                },
                                {
                                    header: 'Rounds count',
                                    cell: (s)=>s.rounds.length
                                },
                                {
                                    header: 'Current holder',
                                    cell: (s)=>s.rounds.find((r)=>r.status === 'current')?.holderLabel || '—'
                                },
                                {
                                    header: 'Actions',
                                    cell: (s)=>canEdit && /*#__PURE__*/ _jsxs(DropdownMenu, {
                                            children: [
                                                /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                                                    asChild: true,
                                                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                                                        size: "xs",
                                                        variant: "secondary",
                                                        "aria-label": `Actions for ${s.name}`,
                                                        children: "Actions"
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                            onSelect: ()=>setAction({
                                                                    kind: 'edit',
                                                                    id: s.id
                                                                }),
                                                            children: "Edit"
                                                        }),
                                                        s.mergedIntoSeatId ? /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                            "data-testid": T.unmergeButton,
                                                            onSelect: ()=>{
                                                                setError('');
                                                                void undo(s.id);
                                                            },
                                                            children: "Undo merge"
                                                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                                            children: [
                                                                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                                    "data-testid": T.roundsButton,
                                                                    onSelect: ()=>setAction({
                                                                            kind: 'rounds',
                                                                            id: s.id
                                                                        }),
                                                                    children: "Rounds…"
                                                                }),
                                                                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                                    "data-testid": T.splitButton,
                                                                    onSelect: ()=>setAction({
                                                                            kind: 'split',
                                                                            id: s.id
                                                                        }),
                                                                    children: "Split…"
                                                                }),
                                                                !s.archivedAt && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                                    "data-testid": T.mergeButton,
                                                                    onSelect: ()=>setAction({
                                                                            kind: 'merge',
                                                                            id: s.id
                                                                        }),
                                                                    children: "Merge into another seat…"
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                            disabled: membership.isPending,
                                                            onSelect: async ()=>{
                                                                try {
                                                                    await membership.mutateAsync({
                                                                        landscapeId,
                                                                        seatId: s.id,
                                                                        member: false
                                                                    });
                                                                } catch (e) {
                                                                    setError(e instanceof Error ? e.message : String(e));
                                                                }
                                                            },
                                                            children: "Remove from landscape"
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                }
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            variant: "group",
                            title: "Suggested rounds"
                        }),
                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "aria-label": "Seat for suggested rounds",
                            value: selected?.id ?? '',
                            onChange: (e)=>setSeatId(e.target.value),
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    disabled: true,
                                    children: "Choose a seat"
                                }),
                                seats.map((s)=>/*#__PURE__*/ _jsx("option", {
                                        value: s.id,
                                        children: s.name
                                    }, s.id))
                            ]
                        }),
                        suggestions.isError && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                            tone: "danger",
                            children: String(suggestions.error)
                        }),
                        suggestions.isLoading && selected ? /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Loading suggestions…"
                        }) : null,
                        /*#__PURE__*/ _jsx(SpecraActionList, {
                            getItemTestId: ()=>T.suggestionRow,
                            trailing: null,
                            items: (suggestions.data?.suggestions ?? []).flatMap((s, i)=>s.round ? [
                                    {
                                        id: String(i),
                                        label: s.round.label || 'Contract round',
                                        description: s.reason,
                                        testId: T.suggestionRow,
                                        actions: /*#__PURE__*/ _jsxs(SpecraButton, {
                                            size: "xs",
                                            "data-testid": T.acceptSuggestion,
                                            disabled: accepting || upsert.isPending || !selected,
                                            onClick: ()=>{
                                                if (s.round) void accept(s.round, s.reason);
                                            },
                                            children: [
                                                "Add to ",
                                                selected?.name
                                            ]
                                        })
                                    }
                                ] : [])
                        })
                    ]
                })
            }),
            adding && /*#__PURE__*/ _jsx(SeatDialog, {
                landscapeId: landscapeId,
                onClose: ()=>setAdding(false)
            }),
            shown && action?.kind === 'edit' && /*#__PURE__*/ _jsx(SeatDialog, {
                landscapeId: landscapeId,
                seat: shown,
                onClose: ()=>setAction(undefined)
            }),
            shown && action?.kind === 'rounds' && /*#__PURE__*/ _jsx(SeatRoundsDialog, {
                seat: shown,
                onClose: ()=>setAction(undefined)
            }),
            shown && canEdit && action?.kind === 'merge' && /*#__PURE__*/ _jsx(MergeSeatDialog, {
                seat: shown,
                seats: seats,
                landscapeId: landscapeId,
                onClose: ()=>setAction(undefined)
            }),
            shown && action?.kind === 'split' && /*#__PURE__*/ _jsx(SplitSeatDialog, {
                seat: shown,
                onClose: ()=>setAction(undefined)
            }),
            unmatched && /*#__PURE__*/ _jsx(SeatRoundForm, {
                seat: unmatched.seat,
                round: unmatched.round,
                awardee: unmatched.awardee,
                onClose: ()=>setUnmatched(undefined)
            })
        ]
    });
}
